// Private Yoga Bath - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const PrivateYogaBath = () => {
  usePageMeta({
    title: "Private Yoga Bath | 1-to-1 Yoga | Annelie Lily",
    description: "Private yoga sessions for Bath residents. One-to-one yoga tailored to your body and goals, based in Frome, 25 minutes from Bath.",
    canonical: "https://annelielily.com/areas/yoga-bath/private-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Private Yoga Sessions for Bath Residents",
    description: "One-to-one private yoga sessions for Bath residents at Annelie Lily Yoga in Frome. Personalised practice tailored to your body, goals, and pace.",
    serviceType: "Private Yoga Sessions",
    url: "https://annelielily.com/areas/yoga-bath/private-yoga"
  });

  const faqs = [
    { question: "What happens in a private yoga session?", answer: "We start with a chat about your goals and any health considerations. Then I design a practice entirely around you — we might focus on strength, flexibility, relaxation, or specific areas you want to work on. Each session responds to how you're feeling that day." },
    { question: "How long are private yoga sessions?", answer: "Sessions are typically 60 minutes, though 90-minute sessions are available if you prefer more time. We work at your pace without rushing." },
    { question: "Is private yoga worth the investment?", answer: "If you have specific goals, injuries to work around, or prefer individual attention, private sessions offer faster progress and guidance tailored to your body. Many students find a few private sessions help them get more from group classes too." },
    { question: "How far is Frome from Bath?", answer: "About 25 minutes via the A36. Many Bath residents make this journey weekly — the drive through the countryside becomes part of the wind-down." },
    { question: "Can I combine private sessions with group classes?", answer: "Absolutely. Some students do regular group classes and occasional private sessions to work on specific areas. Others prefer private sessions exclusively. There's no right or wrong approach." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Private Yoga" currentSlug="areas/yoga-bath/private-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Private Yoga for Bath</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Private Yoga Sessions Bath</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              One-to-one yoga for Bath residents. A practice designed entirely around you — 
              your body, your goals, your pace. Based in Frome, 25 minutes from Bath.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />25 min from Bath</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Private Session <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Snippet / Direct Answer */}
      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is private yoga?</h2>
          <p className="text-neutral-700">
            Private yoga is one-to-one instruction tailored entirely to you. Unlike group classes, every aspect of the session — the poses, pace, and focus — is designed around your body and goals. It's ideal for working with injuries, building confidence, or making faster progress.
          </p>
        </div>
      </section>
      
      {/* Who Is This For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who are private sessions for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Learn the foundations properly before joining a group, or continue one-to-one if you prefer" },
              { title: "Working with injuries", desc: "Adapt practice around back pain, joint issues, or recovering from surgery" },
              { title: "Specific goals", desc: "Whether that's flexibility, strength, stress relief, or preparing for something" },
              { title: "Prefer individual attention", desc: "Some people simply work better one-to-one, without the distraction of others" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a session</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Initial conversation</h3>
                <p className="text-sm">We'll discuss your goals, any health considerations, and what you want from the session. This shapes everything that follows.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Tailored practice</h3>
                <p className="text-sm">Drawing on <Link to="/areas/yoga-bath/hatha-yoga" className="text-gold hover:underline">Hatha</Link>, <Link to="/areas/yoga-bath/yin-yoga" className="text-gold hover:underline">Yin</Link>, or a combination, I'll guide you through a practice designed for your body and energy level that day.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Hands-on guidance</h3>
                <p className="text-sm">With one-to-one attention, I can offer detailed alignment cues and adjustments (with your permission) that aren't possible in a group setting.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Proper relaxation</h3>
                <p className="text-sm">Every session ends with guided relaxation — time for your body to integrate the practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Bath Residents Choose Us */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Bath residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              <Link to="/areas/yoga-bath" className="text-gold hover:underline">Bath</Link> has plenty of yoga studios, but many residents choose to make the 25-minute journey to Frome for private sessions. The A36 route through the countryside is straightforward, and several students tell me the drive becomes part of the transition — leaving work behind, arriving ready to focus on themselves.
            </p>
            <p>
              My studio offers something different from the busier Bath options: a calm, unhurried environment where we can work without interruption. For private sessions especially, that matters.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of private yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, text: "Undivided attention" },
              { icon: Clock, text: "Work at your pace" },
              { icon: Heart, text: "Adapt for your body" },
              { icon: Check, text: "Faster progress" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions about private yoga in Bath</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher with experience working one-to-one with students of all levels and abilities. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to book a private session?</h2>
          <p className="text-neutral-600 mb-6">Get in touch to discuss your goals and find a time that works for you.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Session <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Bath residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-bath/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-bath/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-bath" className="text-sm text-gold hover:underline">All Yoga Classes Bath</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/private-yoga" className="text-sm text-gold hover:underline">Private Sessions (Main Page)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateYogaBath;
